<template>
  <div ref="containerRef" data-testid="scene-preview-cached">
    <div
      class="relative mx-auto bg-black overflow-hidden rounded"
      :style="{
        width: `${scaledWidth}px`,
        height: `${scaledHeight}px`
      }"
    >
      <!-- Cached preview image -->
      <img
        v-if="previewUrl"
        :src="previewUrl"
        class="w-full h-full object-contain"
        alt="Scene preview"
        draggable="false"
      />
      
      <!-- Loading state (visible in viewport but not yet loaded) -->
      <div
        v-else-if="isVisible && !previewChecked"
        class="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground bg-muted/20"
      >
        <span class="animate-pulse">Loading...</span>
      </div>
      
      <!-- No preview placeholder (shown after check completes with no preview) -->
      <div
        v-else-if="previewChecked && !isGenerating"
        class="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground bg-muted/20"
      >
        <span>No layers</span>
      </div>
      
      <!-- Loading/generating state -->
      <div 
        v-if="isGenerating" 
        class="absolute inset-0 bg-background/60 backdrop-blur-[1px] flex items-center justify-center"
      >
        <span class="text-xs text-muted-foreground">Generating...</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useImageCacheStore } from '@/stores/imageCache'
import { useSceneDisplayConfigsStore } from '@/stores/sceneDisplayConfigs'
import { useDisplayLayersStore } from '@/stores/displayLayers'
import { generateScenePreview, needsPreviewGeneration } from '@/services/scenePreviewService'
import type { Display } from '@/stores/displays'

interface Props {
  sceneId: string
  display: Display
  maxWidth?: number
  maxHeight?: number
  isActiveScene?: boolean
  /** Skip lazy loading - for VueFlow nodes that are already visible when mounted */
  eager?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maxWidth: 300,
  maxHeight: 200,
  isActiveScene: false,
  eager: false
})

const configsStore = useSceneDisplayConfigsStore()
const layersStore = useDisplayLayersStore()
const imageCacheStore = useImageCacheStore()

const isGenerating = ref(false)
const containerRef = ref<HTMLElement | null>(null)
// Start visible if eager mode (VueFlow nodes are already visible when mounted)
const isVisible = ref(false)
let observer: IntersectionObserver | null = null

// Get the config for this scene/display
const config = computed(() => 
  configsStore.getConfigForSceneAndDisplay(props.sceneId, props.display.id)
)

// Track if preview generation completed (even with no layers)
const previewChecked = ref(false)

// Check if this config has layers (either direct or inherited)
const hasLayers = computed(() => {
  if (!config.value) return false
  const layers = layersStore.layers.get(config.value.id)
  return layers && layers.length > 0
})

// Get the cached preview URL
const previewUrl = computed(() => {
  // Access cacheVersion for reactivity
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  imageCacheStore.cacheVersion
  
  const storagePath = config.value?.preview_storage_path
  if (!storagePath) return null
  return imageCacheStore.getCachedUrlByPath(storagePath)
})

// Calculate display scaling
const scale = computed(() => {
  const maxWidth = props.maxWidth ?? Infinity
  const maxHeight = props.maxHeight ?? 300
  
  const widthScale = maxWidth / props.display.width
  const heightScale = maxHeight / props.display.height
  
  return Math.min(widthScale, heightScale, 1)
})

const scaledWidth = computed(() => Math.round(props.display.width * scale.value))
const scaledHeight = computed(() => Math.round(props.display.height * scale.value))

// Generate preview if needed
const ensurePreview = async () => {
  if (!config.value) {
    previewChecked.value = true
    return
  }
  
  // Wait for layers store to be initialized before generating
  if (!layersStore.initialized) {
    // Will be called again when stores are ready
    return
  }
  
  const needsGeneration = needsPreviewGeneration(config.value.preview_storage_path)
  
  if (needsGeneration && !isGenerating.value) {
    isGenerating.value = true
    try {
      await generateScenePreview(props.sceneId, props.display.id, props.display)
    } finally {
      isGenerating.value = false
      previewChecked.value = true
    }
  } else {
    previewChecked.value = true
  }
}

// Preload the preview image when visible OR when path changes
// Uses IntersectionObserver for lazy loading - prevents 80 parallel loads
watch(
  () => config.value?.preview_storage_path,
  async (storagePath, oldPath) => {
    // Preload when visible and has path, or when path changes
    if (storagePath && (isVisible.value || (storagePath !== oldPath && oldPath !== undefined))) {
      await imageCacheStore.preloadImagesByPath([storagePath], 'user-images')
    }
  }
)

// When visibility changes, trigger preload if we have a path
watch(isVisible, async (visible) => {
  if (visible) {
    const storagePath = config.value?.preview_storage_path
    if (storagePath) {
      await imageCacheStore.preloadImagesByPath([storagePath], 'user-images')
    }
    ensurePreview()
  }
})

// Check if preview needs to be generated on mount or when config changes
watch(
  () => [props.sceneId, props.display.id, config.value?.id, layersStore.initialized],
  () => {
    // Only check if visible to avoid 80 parallel checks
    if (isVisible.value) {
      ensurePreview()
    }
  },
  { immediate: false }
)

// Setup IntersectionObserver for lazy loading
onMounted(() => {
  // Eager mode: skip lazy loading entirely (for VueFlow nodes that are already visible)
  if (props.eager) {
    isVisible.value = true
    const storagePath = config.value?.preview_storage_path
    if (storagePath) {
      imageCacheStore.preloadImagesByPath([storagePath], 'user-images')
    }
    ensurePreview()
    return
  }
  
  // Check if already visible on mount (e.g., VueFlow only-render-visible-elements)
  // This prevents the "pop-in" effect for nodes that are already in viewport
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100
    if (isInViewport) {
      isVisible.value = true
      // Still trigger preload and preview check
      const storagePath = config.value?.preview_storage_path
      if (storagePath) {
        imageCacheStore.preloadImagesByPath([storagePath], 'user-images')
      }
      ensurePreview()
      return // No need for observer if already visible
    }
  }
  
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true
          // Once visible, we can disconnect - no need to track leaving viewport
          observer?.disconnect()
        }
      })
    },
    { rootMargin: '100px' } // Start loading slightly before visible
  )
  
  if (containerRef.value) {
    observer.observe(containerRef.value)
  }
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>

<style scoped>
/* Minimal styling - let the image do the work */
</style>
